import React from 'react';
import { Tooltip } from 'antd';
import {
  ALL_PROPS,
  KeyframeProp,
  EasingType,
  FRAME_OFFSET,
  useAppStore,
} from '../../store/useAppStore';
import { PROP_LABEL } from './timelineConstants';

export interface KeyframeEasingMenuProps {
  menu: { x: number; y: number; layerId: string; prop: KeyframeProp | 'summary'; frame: number };
  onClose: () => void;
}

const KeyframeEasingMenu = React.memo(({
  menu, onClose,
}: KeyframeEasingMenuProps) => {
  const { x, y, layerId, prop, frame } = menu;
  const kfs              = useAppStore((s) => s.layerKeyframes[layerId]);
  const layerEasings     = useAppStore((s) => s.keyframeEasings[layerId]);
  const setKeyframeEasing = useAppStore((s) => s.setKeyframeEasing);
  const presentProps = prop === 'summary'
    ? ALL_PROPS.filter((p) => { return kfs && frame in kfs[p]; })
    : [prop as KeyframeProp];

  const getEasingVal = (side: 'in' | 'out'): EasingType | null => {
    const vals = presentProps.map((p) => layerEasings?.[p]?.[frame]?.[side] ?? 'linear');
    return vals.every((v) => v === vals[0]) ? vals[0] : null;
  };

  const EASING_OPTIONS: { key: EasingType; label: string; tip: string }[] = [
    { key: 'stepped', label: 'Hold',   tip: 'Hold — value jumps at next keyframe' },
    { key: 'linear',  label: 'Linear', tip: 'Linear — constant rate of change' },
    { key: 'bezier',  label: 'Ease',   tip: 'Ease — smooth acceleration / deceleration' },
  ];

  const MiniShape: React.FC<{ type: EasingType; color?: string }> = ({ type, color = '#aaa' }) => (
    <div style={{
      width: 7, height: 7, flexShrink: 0,
      background: color,
      borderRadius: type === 'bezier' ? '50%' : '1.5px',
      transform: `rotate(${type === 'stepped' ? '0' : '45'}deg)`,
    }} />
  );

  const btnStyle = (active: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 8px', fontSize: 10, cursor: 'pointer', borderRadius: 3,
    marginLeft: 2, userSelect: 'none',
    background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
    color: active ? '#ddd' : '#555',
    border: `1px solid ${active ? '#666' : '#2a2a40'}`,
  });

  return (
    <div
      data-easing-menu="1"
      style={{
        position: 'fixed', left: Math.min(x, window.innerWidth - 190), top: Math.min(y, window.innerHeight - 120),
        background: '#1a1a2e', border: '1px solid #3a3a5a', borderRadius: 6,
        padding: '8px 10px', zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
        minWidth: 178,
      }}
    >
      <div style={{ fontSize: 10, color: '#555', marginBottom: 7, fontFamily: 'monospace' }}>
        f{frame + FRAME_OFFSET}{prop !== 'summary' ? ` · ${PROP_LABEL[prop as KeyframeProp]}` : ` · all props`}
      </div>
      {(['in', 'out'] as const).map((side) => {
        const current = getEasingVal(side) ?? 'linear';
        return (
          <div key={side} style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, width: 56, flexShrink: 0 }}>
              <MiniShape type={current} color="#777" />
              <span style={{ fontSize: 10, color: '#666' }}>
                {side === 'in' ? '◄ in:' : '► out:'}
              </span>
            </div>
            {EASING_OPTIONS.map(({ key, label, tip }) => (
              <Tooltip key={key} title={tip} placement="top">
                <div
                  style={btnStyle(current === key)}
                  onClick={() => {
                    presentProps.forEach((p) => setKeyframeEasing(layerId, p, frame, { [side]: key }));
                    onClose();
                  }}
                >
                  <MiniShape type={key} color={current === key ? '#ddd' : '#555'} />
                  {label}
                </div>
              </Tooltip>
            ))}
          </div>
        );
      })}
    </div>
  );
});

export default KeyframeEasingMenu;
